// src/components/FarmMark/EarnIPTips.js
//
// Desktop-only explanatory column for FarmMark.
// Three concise IP-earning pathways backed by real existing routes and rules.
// No invented rewards — amounts match impactPointsService ACTION_CONFIG.
// Buttons match the Orrith landing page convention: fit the text, no full width.

import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";

function EarnCard({ title, body, actionLabel, to, onClick, disabled }) {
  const content = (
    <span className="inline-flex items-center gap-1.5">
      {actionLabel}
    </span>
  );

  const baseClass =
    "ui-btn-primary px-4 py-2 text-sm font-semibold rounded-full inline-flex items-center gap-1.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600";

  const action = to ? (
    disabled ? (
      <span className={`${baseClass} opacity-60 cursor-not-allowed pointer-events-none`}>
        {content}
      </span>
    ) : (
      <Link to={to} className={baseClass}>
        {content}
      </Link>
    )
  ) : (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={disabled ? `${baseClass} opacity-60 cursor-not-allowed` : baseClass}
    >
      {content}
    </button>
  );

  return (
    <div className="rounded-xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1.5">
        {title}
      </h3>
      <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
        {body}
      </p>
      <div className="flex justify-center">
        {action}
      </div>
    </div>
  );
}

export default function EarnIPTips() {
  const { t } = useTranslation("farmMark");
  const { user } = useAuth();

  const handleFeedback = () => {
    if (!user) return;
    window.dispatchEvent(new CustomEvent("orrith-feedback-open"));
  };

  return (
    <div className="space-y-3">
      <div className="text-center">
        <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {t("earn.title", { defaultValue: "How to earn IP" })}
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {t("earn.subtitle", { defaultValue: "Impact Points reward real contributions." })}
        </p>
      </div>

      <EarnCard
        title={t("earn.contribute.title", { defaultValue: "Contribute" })}
        body={t("earn.contribute.body", {
          defaultValue:
            "Submit crop prices and planting reports through FarmPulse. Validated price reports earn 10 IP.",
        })}
        actionLabel={t("earn.contribute.action", { defaultValue: "Contribute" })}
        to="/farmpulse?tab=contribute"
      />

      <EarnCard
        title={t("earn.market.title", { defaultValue: "Buy, sell & build trust" })}
        body={t("earn.market.body", {
          defaultValue:
            "List products on the Farm Market and earn 5 IP per listing. Eligible ratings also contribute.",
        })}
        actionLabel={t("earn.market.action", { defaultValue: "Visit Farm Market" })}
        to="/farm"
      />

      <EarnCard
        title={t("earn.feedback.title", { defaultValue: "Share useful feedback" })}
        body={t("earn.feedback.body", {
          defaultValue:
            "Report problems or suggest improvements. Useful feedback may qualify for 10 IP when reviewed by the Orrith team.",
        })}
        actionLabel={t("earn.feedback.action", { defaultValue: "Send feedback" })}
        onClick={handleFeedback}
        disabled={!user}
      />
    </div>
  );
}